import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { customersAPI, projectsAPI, adminAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { SortableTableHead, useTableSort } from "../components/ui/sortable-table-head";
import { toast } from "sonner";
import { Plus, Search, Users, Mail, Phone, MapPin, Eye, Trash2, Upload, FileSpreadsheet, CheckCircle, XCircle, AlertCircle } from "lucide-react";

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    salutation: "",
    first_name: "",
    last_name: "",
    address: "",
    phone: "",
    email: "",
    tax_number: "",
    bank_iban: "",
    bank_bic: "",
    bank_name: "",
    company_name: ""
  });
  const [saving, setSaving] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [creatingProjects, setCreatingProjects] = useState(false);
  const navigate = useNavigate();
  
  // Sorting state - default to newest first (created_at descending)
  const { sortColumn, sortDirection, handleSort, sortData } = useTableSort("created_at", "desc");

  // Check if "Firma" is selected
  const isCompany = formData.salutation === "Firma";

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      const response = await customersAPI.list();
      setCustomers(response.data);
    } catch (error) {
      console.error("Failed to load customers:", error);
      toast.error("Fehler beim Laden der Kunden");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validierung: Bei Firma muss company_name ausgefüllt sein
    if (!formData.salutation || !formData.first_name || !formData.last_name || !formData.address) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus");
      return;
    }
    
    if (formData.salutation === "Firma" && !formData.company_name) {
      toast.error("Bitte geben Sie einen Firmennamen ein");
      return;
    }
    
    setSaving(true);
    
    try {
      // Create customer
      const customerResponse = await customersAPI.create(formData);
      const customer = customerResponse.data;
      
      // Automatically create a project for the customer
      await projectsAPI.create({
        customer_id: customer.id,
        notes: ""
      });
      
      toast.success("Kunde und Projekt erfolgreich angelegt");
      setDialogOpen(false);
      resetForm();
      loadCustomers();
      
      // Navigate to customer detail
      navigate(`/kunden/${customer.id}`);
    } catch (error) {
      console.error("Failed to create customer:", error);
      // Handle Pydantic validation errors (array format)
      const errorDetail = error.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        const errorMessages = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(", ");
        toast.error(errorMessages || "Validierungsfehler");
      } else if (typeof errorDetail === 'string') {
        toast.error(errorDetail);
      } else {
        toast.error("Fehler beim Anlegen des Kunden");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm("Möchten Sie diesen Kunden wirklich löschen?")) return;
    
    try {
      await customersAPI.delete(customerId);
      toast.success("Kunde gelöscht");
      loadCustomers();
    } catch (error) {
      console.error("Failed to delete customer:", error);
      toast.error("Fehler beim Löschen des Kunden");
    }
  };

  const handleImportCSV = async () => {
    if (!importFile) {
      toast.error("Bitte wählen Sie eine CSV-Datei aus");
      return;
    }
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const response = await customersAPI.importCSV(importFile);
      setImportResult(response.data);
      
      if (response.data.imported > 0) {
        toast.success(`${response.data.imported} Kunden erfolgreich importiert`);
        loadCustomers();
      }
      
      if (response.data.skipped > 0) {
        toast.warning(`${response.data.skipped} Einträge übersprungen`);
      }
    } catch (error) {
      console.error("CSV Import failed:", error);
      toast.error(error.response?.data?.detail || "Fehler beim CSV-Import");
    } finally {
      setImporting(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error("Bitte wählen Sie eine CSV-Datei aus");
        return;
      }
      setImportFile(file);
      setImportResult(null);
    }
  };

  const closeImportDialog = () => {
    setImportDialogOpen(false);
    setImportFile(null);
    setImportResult(null);
  };

  const handleCreateMissingProjects = async () => {
    setCreatingProjects(true);
    try {
      const response = await adminAPI.createMissingProjects();
      const { created_count, message } = response.data;
      
      if (created_count > 0) {
        toast.success(message);
      } else {
        toast.info("Alle Kunden haben bereits ein Projekt");
      }
    } catch (error) {
      console.error("Failed to create missing projects:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen der Projekte");
    } finally {
      setCreatingProjects(false);
    }
  };

  const resetForm = () => {
    setFormData({
      salutation: "",
      first_name: "",
      last_name: "",
      address: "",
      phone: "",
      email: "",
      tax_number: "",
      bank_iban: "",
      bank_bic: "",
      bank_name: "",
      company_name: ""
    });
  };

  const filteredCustomers = customers.filter(customer => {
    const searchLower = searchTerm.toLowerCase();
    return (
      customer.first_name?.toLowerCase().includes(searchLower) ||
      customer.last_name?.toLowerCase().includes(searchLower) ||
      customer.customer_number?.toLowerCase().includes(searchLower) ||
      customer.email?.toLowerCase().includes(searchLower) ||
      customer.company_name?.toLowerCase().includes(searchLower)
    );
  });

  // Apply sorting to filtered customers
  const sortedCustomers = sortData(filteredCustomers, {
    // Custom comparator for name (combines company_name or first/last name)
    name: (a, b) => {
      const aName = (a.company_name || `${a.first_name} ${a.last_name}`).toLowerCase();
      const bName = (b.company_name || `${b.first_name} ${b.last_name}`).toLowerCase();
      return aName.localeCompare(bName);
    }
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Kunden</h1>
          <p className="text-muted-foreground">
            Verwalten Sie Ihre Kundendaten
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleCreateMissingProjects} 
            disabled={creatingProjects}
            data-testid="create-missing-projects-btn"
          >
            {creatingProjects ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Erstelle Projekte...
              </>
            ) : (
              <>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Fehlende Projekte erstellen
              </>
            )}
          </Button>
          <Button variant="outline" onClick={() => setImportDialogOpen(true)} data-testid="import-csv-btn">
            <Upload className="h-4 w-4 mr-2" />
            CSV Import
          </Button>
          <Button onClick={() => setDialogOpen(true)} data-testid="new-customer-btn">
            <Plus className="h-4 w-4 mr-2" />
            Neuer Kunde
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Suche nach Name, Kundennummer, E-Mail..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 h-12"
          data-testid="customer-search"
        />
      </div>

      {/* Customers List */}
      {sortedCustomers.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead
                    column="customer_number"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  >
                    Kundennr.
                  </SortableTableHead>
                  <SortableTableHead
                    column="name"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  >
                    Name
                  </SortableTableHead>
                  <TableHead className="hidden md:table-cell">E-Mail</TableHead>
                  <TableHead className="hidden md:table-cell">Telefon</TableHead>
                  <SortableTableHead
                    column="created_at"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    className="hidden lg:table-cell"
                  >
                    Erstellt
                  </SortableTableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedCustomers.map((customer) => (
                  <TableRow key={customer.id} className="cursor-pointer hover:bg-muted/50">
                    <TableCell className="font-mono text-sm">
                      {customer.customer_number}
                    </TableCell>
                    <TableCell>
                      <div>
                        {customer.company_name ? (
                          <>
                            <p className="font-medium">{customer.company_name}</p>
                            <p className="text-sm text-muted-foreground">
                              {customer.first_name} {customer.last_name}
                            </p>
                          </>
                        ) : (
                          <p className="font-medium">
                            {customer.salutation} {customer.first_name} {customer.last_name}
                          </p>
                        )}
                        <p className="text-sm text-muted-foreground md:hidden">
                          {customer.email}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {customer.email && (
                        <span className="flex items-center gap-1 text-sm">
                          <Mail className="h-3 w-3" />
                          {customer.email}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {customer.phone && (
                        <span className="flex items-center gap-1 text-sm">
                          <Phone className="h-3 w-3" />
                          {customer.phone}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                      {customer.created_at && new Date(customer.created_at).toLocaleDateString("de-DE")}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          asChild
                          data-testid={`view-customer-${customer.id}`}
                        >
                          <Link to={`/kunden/${customer.id}`}>
                            <Eye className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(customer.id)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-customer-${customer.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card className="py-12">
          <CardContent className="text-center">
            <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-semibold mb-2">Keine Kunden gefunden</h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm ? "Versuchen Sie eine andere Suche" : "Legen Sie Ihren ersten Kunden an"}
            </p>
            {!searchTerm && (
              <Button onClick={() => setDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Neuer Kunde
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* New Customer Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Neuer Kunde</DialogTitle>
            <DialogDescription>
              Erfassen Sie die Kundendaten. Ein Projekt wird automatisch angelegt.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Info */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">
                {isCompany ? "Firmendaten" : "Persönliche Daten"}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="salutation">Anrede *</Label>
                  <Select
                    value={formData.salutation}
                    onValueChange={(value) => setFormData({ ...formData, salutation: value, company_name: value !== "Firma" ? "" : formData.company_name })}
                  >
                    <SelectTrigger id="salutation" data-testid="salutation-select">
                      <SelectValue placeholder="Bitte wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Herr">Herr</SelectItem>
                      <SelectItem value="Frau">Frau</SelectItem>
                      <SelectItem value="Divers">Divers</SelectItem>
                      <SelectItem value="Firma">Firma</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="first_name">{isCompany ? "Ansprechpartner Vorname *" : "Vorname *"}</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    placeholder={isCompany ? "Thomas" : "Max"}
                    data-testid="first-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="last_name">{isCompany ? "Ansprechpartner Nachname *" : "Nachname *"}</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    placeholder={isCompany ? "Müller" : "Mustermann"}
                    data-testid="last-name-input"
                  />
                </div>
              </div>
              
              {/* Firmenname - nur bei Anrede "Firma" */}
              {isCompany && (
                <div className="space-y-2">
                  <Label htmlFor="company_name">Firmenname *</Label>
                  <Input
                    id="company_name"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    placeholder="Musterfirma GmbH"
                    data-testid="company-name-input"
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="address">Adresse *</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Musterstraße 123, 12345 Musterstadt"
                  data-testid="address-input"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefonnummer</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+49 123 456789"
                    data-testid="phone-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">E-Mail</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="max@beispiel.de"
                    data-testid="email-input"
                  />
                </div>
              </div>
            </div>

            {/* Tax & Bank Info */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">
                Steuer & Bankverbindung
              </h3>
              
              <div className="space-y-2">
                <Label htmlFor="tax_number">Steuernummer</Label>
                <Input
                  id="tax_number"
                  value={formData.tax_number}
                  onChange={(e) => setFormData({ ...formData, tax_number: e.target.value })}
                  placeholder="12/345/67890"
                  data-testid="tax-number-input"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="bank_iban">IBAN</Label>
                  <Input
                    id="bank_iban"
                    value={formData.bank_iban}
                    onChange={(e) => setFormData({ ...formData, bank_iban: e.target.value })}
                    placeholder="DE89 3704 0044 0532 0130 00"
                    data-testid="iban-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="bank_bic">BIC</Label>
                  <Input
                    id="bank_bic"
                    value={formData.bank_bic}
                    onChange={(e) => setFormData({ ...formData, bank_bic: e.target.value })}
                    placeholder="COBADEFFXXX"
                    data-testid="bic-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="bank_name">Bank</Label>
                <Input
                  id="bank_name"
                  value={formData.bank_name}
                  onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                  placeholder="Commerzbank"
                  data-testid="bank-name-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-customer-btn">
                {saving ? "Speichern..." : "Kunde anlegen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={closeImportDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              CSV-Kontaktimport
            </DialogTitle>
            <DialogDescription>
              Importieren Sie Leads aus einer CSV-Datei (z.B. von Photovoltaik-Angebotsvergleich)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* File Upload Area */}
            <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
                id="csv-file-input"
                data-testid="csv-file-input"
              />
              <label 
                htmlFor="csv-file-input"
                className="cursor-pointer flex flex-col items-center gap-2"
              >
                <Upload className="h-8 w-8 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Klicken Sie hier oder ziehen Sie eine CSV-Datei hierher
                </span>
              </label>
              
              {importFile && (
                <div className="mt-4 p-3 bg-muted rounded-lg flex items-center justify-center gap-2">
                  <FileSpreadsheet className="h-4 w-4" />
                  <span className="text-sm font-medium">{importFile.name}</span>
                  <span className="text-xs text-muted-foreground">
                    ({(importFile.size / 1024).toFixed(1)} KB)
                  </span>
                </div>
              )}
            </div>
            
            {/* Expected Format Info */}
            <div className="bg-blue-50 dark:bg-blue-950/30 p-4 rounded-lg text-sm">
              <h4 className="font-medium mb-2">Erwartetes CSV-Format:</h4>
              <ul className="text-muted-foreground space-y-1 text-xs">
                <li>• Trennzeichen: Semikolon (;)</li>
                <li>• Erforderliche Spalten: <span className="font-mono">Vorname</span>, <span className="font-mono">Nachname</span></li>
                <li>• Optionale Spalten: Anrede, Firma, Straße und Hausnummer, PLZ, Ort, Kontaktnummer, E-Mail</li>
                <li>• Zusätzliche Infos (Stromverbrauch, Dachfläche, etc.) werden als Notizen gespeichert</li>
              </ul>
            </div>
            
            {/* Import Result */}
            {importResult && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-green-50 dark:bg-green-950/30 p-3 rounded-lg flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="text-sm font-medium">{importResult.imported} Importiert</p>
                      <p className="text-xs text-muted-foreground">Neue Kunden angelegt</p>
                    </div>
                  </div>
                  
                  {importResult.skipped > 0 && (
                    <div className="bg-yellow-50 dark:bg-yellow-950/30 p-3 rounded-lg flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-yellow-600" />
                      <div>
                        <p className="text-sm font-medium">{importResult.skipped} Übersprungen</p>
                        <p className="text-xs text-muted-foreground">Fehler oder Duplikate</p>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Error List */}
                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="bg-red-50 dark:bg-red-950/30 p-3 rounded-lg">
                    <p className="text-sm font-medium text-red-700 dark:text-red-400 mb-2 flex items-center gap-1">
                      <XCircle className="h-4 w-4" />
                      Fehler beim Import:
                    </p>
                    <ul className="text-xs text-red-600 dark:text-red-400 space-y-1 max-h-32 overflow-y-auto">
                      {importResult.errors.map((error, idx) => (
                        <li key={idx}>• {error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeImportDialog}>
              {importResult ? "Schließen" : "Abbrechen"}
            </Button>
            {!importResult && (
              <Button 
                onClick={handleImportCSV} 
                disabled={!importFile || importing}
                data-testid="start-import-btn"
              >
                {importing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Importiere...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Import starten
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
